/* lig-card-maker — "A Very Golden Christmas" card packs with the customer's own dog.
 *
 * 1 Scene → 2 Your dogs (1–3, one photo each → painted into the scene by {middleware}/api/card/design,
 * ~1 min, sized by breed; or keep our golden) → 3 The words inside (+ an inky paw print) → See my card
 * → 25 cards to the cart with `_card_id` (the ONLY thing that tells the middleware what to print;
 * lib/cardMaker.ts) → "Make it a set": the mug (their card's painting, ready with the card) and ONE
 * ORNAMENT PER DOG (painted a minute later), each ornament line tagged `_dog` = 1..3.
 *
 * A card design is immutable: change a word, the paw, the scene or the dog and it is set again
 * before it can go in the cart. No express checkout here on purpose — it drops line properties.
 * Cart add mirrors lig-ornament-maker.js: /cart/add.js with section ids, CartAddEvent so the
 * drawer morphs, then drawer.open().
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

const STORE_KEY = 'lig_card_maker_v2';
const DOWNSCALE_MAX_EDGE = 2000, DOWNSCALE_QUALITY = 0.86;
const money = (cents) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

class CardMaker {
  constructor(root) {
    this.root = root;
    this.mw = root.dataset.middleware.replace(/\/$/, '');
    this.api = this.mw + '/api/card/design';
    this.giftMode = new URLSearchParams(location.search).get('gift') === 'mug' ? 'mug' : 'card';
    this.fromCalendar = new URLSearchParams(location.search).get('from') === 'calendar';
    this.privateIntake = this.fromCalendar; this.pollEpoch = 0; this.restoringDesign = null;
    this.freeShip = Number(root.dataset.freeShip || 99) * 100;
    const json = (sel) => { try { return JSON.parse(root.querySelector(sel)?.textContent || 'null'); } catch { return null; } };
    this.products = { card: json('[data-cm-card]') || [], ornament: json('[data-cm-ornament]') || [], mug: json('[data-cm-mug]') || [] };
    const $ = (s) => root.querySelector(s);
    this.el = {
      scenes: $('[data-cm-scenes]'), modes: root.querySelectorAll('[name="cm-mode"]'), dogBox: $('[data-cm-dogbox]'), goldenNote: $('[data-cm-golden-note]'),
      drops: $('[data-cm-drops]'), dropTpl: $('[data-cm-drop-tpl]'), counts: root.querySelectorAll('[name="cm-count"]'), consent: $('[name="cm-consent"]'), paint: $('[data-cm-paint]'),
      again: $('[data-cm-again]'), dogStatus: $('[data-cm-dogstatus]'), bar: $('[data-cm-bar]'),
      greetings: $('[data-cm-greetings]'), headline: $('[name="cm-headline"]'), body: $('[name="cm-body"]'), signoff: $('[name="cm-signoff"]'), names: $('[name="cm-names"]'),
      paw: $('[name="cm-paw"]'), hp: $('[name="hp_4c1"]'), see: $('[data-cm-see]'),
      stage: $('[data-cm-stage-img]'), stageCap: $('[data-cm-stage-cap]'), busy: $('[data-cm-busy]'), busyText: $('[data-cm-busy-text]'), status: $('[data-cm-status]'), dropped: $('[data-cm-dropped]'),
      price: $('[data-cm-price]'), qty: $('[name="cm-qty"]'), add: $('[data-cm-add]'), total: $('[data-cm-total]'),
      set: $('[data-cm-set]'), orns: $('[data-cm-orns]'), mugImg: $('[data-cm-mug-img]'), addMug: $('[data-cm-add-mug]'),
      mugSize: $('[name="cm-mug-size"]'), ship: $('[data-cm-ship]'), setWait: $('[data-cm-set-wait]'), cartContinuation: $('[data-cm-cart-continuation]'), counters: root.querySelectorAll('[data-cm-count]'),
    };
    this.state = { manifest: null, scene: null, mode: 'dog', photoUrls: [], count: 1, dogs: {}, dog: null, ornKey: '', greeting: 'merry', design: null, designKey: '', busy: false, cartBusy: false, uploading: false, poll: null, inCart: false };
    this.init();
  }

  async init() {
    try {
      const r = await fetch(this.api, { headers: { Accept: 'application/json' } });
      this.state.manifest = await r.json();
      if (!r.ok || !this.state.manifest.ok) throw new Error();
    } catch {
      this.status(this.fromCalendar && this.giftMode === 'mug' ? 'The mug studio could not load — refresh the page and try again. Your calendar stays saved.' : 'The card maker could not load — refresh the page, or write to us and we will set your cards for you.', true);
      return;
    }
    const handoff = this.fromCalendar ? window.LigDogHandoff?.consume(this.giftMode) : null;
    const saved = handoff ? {} : this.load();
    this.privateIntake = this.fromCalendar || !!saved.privateIntake;
    const urls = Array.isArray(saved.photoUrls) ? saved.photoUrls : saved.photoUrl ? [saved.photoUrl] : [];
    Object.assign(this.state, { photoUrls: urls.slice(0, 3), count: Math.max(1, Math.min(3, Number(saved.count) || urls.length || 1)), dogs: saved.dogs || {}, mode: saved.mode === 'golden' ? 'golden' : 'dog' });
    this.renderScenes(saved.scene);
    this.renderGreetings(saved.greeting);
    if (saved.words) this.setWords(saved.words); else this.applyGreeting(this.state.greeting);
    if (typeof saved.paw === 'boolean') this.el.paw.checked = saved.paw;
    this.el.modes.forEach((m) => { m.checked = m.value === this.state.mode; m.addEventListener('change', () => { if (m.checked) { this.state.mode = m.value; this.save(); this.showMode(); this.sync(); } }); });
    this.el.counts.forEach((c) => { c.checked = Number(c.value) === this.state.count; c.addEventListener('change', () => { if (c.checked) this.setCount(Number(c.value)); }); });
    this.renderDrops();
    this.el.paint.addEventListener('click', (e) => { e.preventDefault(); this.paint(); });
    this.el.again.addEventListener('click', (e) => { e.preventDefault(); this.again(); });
    ['headline', 'body', 'signoff', 'names'].forEach((k) => this.el[k].addEventListener('input', () => { this.counts(); this.save(); this.sync(); }));
    this.el.paw.addEventListener('change', () => { this.save(); this.sync(); });
    this.el.consent.addEventListener('change', () => this.sync());
    this.el.see.addEventListener('click', (e) => { e.preventDefault(); this.preview(); });
    this.el.add.addEventListener('click', (e) => { e.preventDefault(); this.addCard(); });
    this.el.addMug.addEventListener('click', (e) => { e.preventDefault(); this.addMatch('mug'); });
    this.el.qty.addEventListener('change', () => this.sync());
    this.counts();
    this.showMode();
    const dogId = this.state.dogs[this.state.scene];
    if (dogId) this.watchDog(dogId, false);
    if (saved.designId) this.restoreDesign(saved.designId);
    this.refreshShip();
    this.sync();
    // "Customize this scene" from the landing page's close-up
    const pick = (id) => {
      const input = this.root.querySelector(`input[name="cm-scene"][value="${CSS.escape(id)}"]`);
      if (!input) return;
      input.checked = true;
      if (id !== this.state.scene) this.pickScene(id);
    };
    document.addEventListener('lig-cm:pick', (e) => pick(e.detail && e.detail.scene));
    if (window.__ligCmPick) pick(window.__ligCmPick);
    if (this.fromCalendar) this.calendarMode();
    if (handoff) {
      this.privateIntake = true;
      this.state.mode = 'dog'; this.state.count = 1;
      this.state.photoUrls = []; this.state.dogs = {}; this.state.dog = null;
      this.state.design = null; this.state.designKey = '';
      this.el.modes.forEach(m => { m.checked = m.value === 'dog'; });
      this.el.counts.forEach(c => { c.checked = c.value === '1'; });
      this.pickScene(handoff.scene); this.renderDrops(); this.showMode();
      this.el.names.value = handoff.name; this.el.consent.checked = true;
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      try {
        await this.upload(window.LigDogHandoff.toFile(handoff.photo), 0, this.el.drops.firstElementChild);
        if (this.photosReady()) await this.paint();
      } catch (error) { this.dogStatus(error.message || 'Please choose your photo again.', true); }
    }
  }

  calendarMode() {
    if (!this.root.querySelector('[data-calendar-return]')) {
      const link = document.createElement('a'); link.href = '/pages/your-dog-calendar';
      link.textContent = '← Back to your saved calendar'; link.dataset.calendarReturn = '';
      link.style.cssText = 'display:inline-block;margin:0 0 20px;color:inherit';
      this.root.querySelector('.lig-om__wrap').prepend(link);
    }
    this.el.orns.hidden = true;
    if (this.giftMode !== 'mug') return;
    const eyebrow = this.root.querySelector('.lig-om__eyebrow');
    if (eyebrow) eyebrow.textContent = 'Your dog’s Christmas mug';
    this.root.querySelector('.lig-om__heading').textContent = 'A Christmas mug, starring your dog.';
    this.root.querySelector('.lig-om__intro').textContent = 'Choose a watercolor scene. We’ll paint your dog into artwork made for a mug, and show you the result before you buy.';
    this.root.querySelector('.lig-om__hero .lig-om__price')?.setAttribute('hidden', '');
    this.root.querySelector('[name="cm-headline"]').closest('.lig-om__step').hidden = true;
    this.el.add.closest('.lig-om__step').hidden = true;
    this.root.querySelector('.lig-cm__modes').hidden = true;
    this.el.orns.hidden = true;
    this.el.set.querySelector('.lig-om__step-title').textContent = 'Your mug preview';
    this.el.set.querySelector('.lig-cm__fine').textContent = 'Check your dog’s likeness, then choose your mug size.';
    this.el.set.querySelector('.lig-cm__set-grid').style.gridTemplateColumns = '1fr';
    this.el.mugSize.addEventListener('change', () => this.sync());
    const retry = document.createElement('button'); retry.type = 'button';
    retry.className = 'lig-cm__btn'; retry.textContent = 'See my mug preview'; retry.dataset.mugPreview = '';
    retry.addEventListener('click', () => this.preview());
    this.el.status.after(retry); this.el.mugPreview = retry;
    this.el.paw.checked = false;
    if (!this.el.names.value) this.el.names.value = 'Your dog';
    if (this.dogReady() && !this.fresh() && !this.restoringDesign) this.preview();
  }

  // ---- scenes ----------------------------------------------------------------------
  scene() { return (this.state.manifest.scenes || []).find((s) => s.id === this.state.scene) || null; }

  renderScenes(pick) {
    const scenes = this.state.manifest.scenes || [];
    this.state.scene = (scenes.find((s) => s.id === pick) || scenes[0] || {}).id || null;
    this.el.scenes.innerHTML = '';
    for (const s of scenes) {
      const label = document.createElement('label');
      label.className = 'lig-cm__front';
      const room = s.max_dogs || 1;
      label.innerHTML = `<input type="radio" name="cm-scene" value="${esc(s.id)}"><img src="${esc(s.thumb_url)}" alt="" width="400" height="562" loading="lazy"><b>${esc(s.label)}</b><small>${esc(s.blurb)}</small><small class="lig-cm__room">Room for ${room === 1 ? '1 dog' : `up to ${room} dogs`}</small>`;
      label.dataset.room = String(room);
      const input = label.querySelector('input');
      input.checked = s.id === this.state.scene;
      input.addEventListener('change', () => this.pickScene(s.id));
      this.el.scenes.append(label);
    }
    this.markRoom();
    this.showStage();
  }
  /** Scenes without room for this many dogs are dimmed and can't be picked. */
  markRoom() {
    const n = this.state.mode === 'dog' ? this.state.count : 1;
    this.el.scenes.querySelectorAll('label').forEach((l) => { const tight = Number(l.dataset.room || 1) < n; l.classList.toggle('is-tight', tight); l.querySelector('input').disabled = tight; });
    const cur = this.scene();
    if (cur && (cur.max_dogs || 1) < n) {
      const fit = (this.state.manifest.scenes || []).find((x) => (x.max_dogs || 1) >= n);
      if (fit) { const inp = this.el.scenes.querySelector(`input[value="${CSS.escape(fit.id)}"]`); if (inp) inp.checked = true; this.pickScene(fit.id); }
    }
  }

  pickScene(id) {
    this.state.scene = id;
    this.state.busy = false;
    this.stopPoll();
    this.state.dog = null;
    this.save();
    const dogId = this.state.dogs[id];
    if (dogId) this.watchDog(dogId, false);
    else if (this.state.mode === 'dog' && this.photosReady()) this.dogStatus(`Paint ${this.state.count > 1 ? 'your dogs' : 'your dog'} into ${this.scene()?.label || 'this scene'} — tap "Paint my dog${this.state.count > 1 ? 's' : ''}".`);
    this.showStage();
    this.sync();
  }

  showMode() {
    const dog = this.state.mode === 'dog';
    this.el.dogBox.hidden = !dog;
    this.el.goldenNote.hidden = dog;
    this.markRoom();
    this.showStage();
  }

  showStage() {
    const s = this.scene();
    if (this.state.design && this.fresh()) { this.stageImg(this.giftMode === 'mug' ? (this.state.design.mug_preview_url || this.state.design.preview_url) : this.state.design.preview_url, this.giftMode === 'mug' ? 'Your dog’s Christmas mug' : 'Your card: the front and your inside page'); return; }
    if (this.state.mode === 'dog' && this.state.dog && this.state.dog.status === 'ready' && this.state.dog.scene === this.state.scene && this.state.dog.front_url) { this.stageImg(this.state.dog.front_url, 'Your dog, painted into the scene'); return; }
    if (this.giftMode === 'mug' && this.fresh() && this.state.design.mug_preview_url) { this.stageImg(this.state.design.mug_preview_url, 'Your dog’s Christmas mug'); return; }
    if (s) this.stageImg(s.thumb_url, `${s.label} — our golden`);
  }
  stageImg(src, cap) { this.el.stage.src = src; this.el.stage.alt = cap; this.el.stage.hidden = false; this.el.stageCap.textContent = cap; }

  // ---- your dog ----------------------------------------------------------------------
  /** One photo box per dog (1–3). Dog 1 holds the ornament. */
  renderDrops() {
    const n = this.state.count;
    this.el.drops.style.setProperty('--cm-n', String(n));
    this.el.drops.innerHTML = '';
    for (let i = 0; i < n; i++) {
      const node = this.el.dropTpl.content.firstElementChild.cloneNode(true);
      const input = node.querySelector('input'), img = node.querySelector('img');
      node.querySelector('[data-cm-drop-title]').textContent = n === 1 ? 'Add a photo of your dog' : `Dog ${i + 1}${i === 0 ? ' · holds the ornament' : ''}`;
      img.alt = n === 1 ? "Your dog's photo" : `Dog ${i + 1}'s photo`;
      if (this.state.photoUrls[i]?.includes('.private.blob.vercel-storage.com/')) node.querySelector('[data-cm-drop-title]').textContent = 'Saved photo ready — choose a different one?';
      if (this.state.photoUrls[i] && !this.state.photoUrls[i].includes('.private.blob.vercel-storage.com/')) { img.src = this.state.photoUrls[i]; img.hidden = false; }
      input.addEventListener('change', () => this.upload(input.files && input.files[0], i, node));
      this.el.drops.append(node);
    }
    this.el.paint.textContent = n > 1 ? 'Paint my dogs' : 'Paint my dog';
  }
  setCount(n) {
    this.state.busy = false;
    this.state.count = Math.max(1, Math.min(3, n));
    this.state.dogs = {};             // a different pack: every scene paints fresh
    this.stopPoll(); this.state.dog = null;
    this.save(); this.renderDrops(); this.markRoom(); this.showStage(); this.sync();
    this.dogStatus(this.photosReady() ? `Tap "Paint my dog${n > 1 ? 's' : ''}".` : `Add ${n > 1 ? 'a photo of each dog' : 'a photo of your dog'} — one dog per photo.`);
  }
  photosReady() { return this.state.photoUrls.slice(0, this.state.count).filter(Boolean).length === this.state.count; }

  async upload(file, i, node) {
    if (!file || this.state.uploading) return;
    this.stopPoll(); this.state.dogs = {}; this.state.dog = null; this.state.design = null; this.state.designKey = ''; this.state.busy = false;
    this.state.uploading = true; this.sync();
    node.classList.add('is-busy'); this.dogStatus('Uploading your photo…');
    try {
      const prepared = await downscale(file);
      if (!prepared) throw new Error('That photo is too large — try one under 12 MB.');
      const fname = `card-dog.${prepared.ext}`;
      const res = await fetch(`${this.mw}/api/custom-design/upload?filename=${encodeURIComponent(fname)}${this.privateIntake ? '&private=1' : ''}`, { method: 'POST', headers: { 'Content-Type': prepared.contentType }, body: prepared.blob });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || 'The photo would not upload — try again.');
      this.state.photoUrls[i] = data.url;
      this.state.dogs = {};             // a new photo: every scene paints fresh
      this.state.dog = null;
      const img = node.querySelector('img'); img.src = URL.createObjectURL(file); img.hidden = false;
      this.save();
      this.dogStatus(this.photosReady() ? `Got it. Tap "Paint my dog${this.state.count > 1 ? 's' : ''}" — it takes about a minute.` : 'Got it — now the next dog.');
    } catch (err) {
      this.dogStatus(err.message || 'The photo would not upload — try again.', true);
    } finally {
      node.classList.remove('is-busy'); this.state.uploading = false; this.sync();
    }
  }

  async paint() {
    if (!this.photosReady()) { this.dogStatus(this.state.count > 1 ? 'Add a photo of each dog first.' : 'Add a photo of your dog first.', true); return; }
    if (!this.el.consent.checked) { this.dogStatus('Please confirm you can use these photos.', true); return; }
    const urls = this.state.photoUrls.slice(0, this.state.count);
    await this.dogCall({ action: 'dog', scene: this.state.scene, photo_urls: urls, photo_url: urls[0] });
  }
  async again() {
    const d = this.state.dog;
    if (!d) return;
    await this.dogCall({ action: 'redog', id: d.id });
  }
  async dogCall(body) {
    this.stopPoll(); const epoch = this.pollEpoch;
    const source = JSON.stringify([this.state.scene,this.state.photoUrls,this.state.count]);
    this.state.busy = true; this.sync();
    try {
      const r = await fetch(this.api, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, ...(this.fromCalendar ? { product_intent: this.giftMode } : {}), hp_4c1: this.el.hp?.value || '' }) });
      const j = await r.json().catch(() => ({}));
      if (epoch !== this.pollEpoch || source !== JSON.stringify([this.state.scene,this.state.photoUrls,this.state.count])) return;
      if (!r.ok || !j.dog) throw new Error(j.error || 'The drawing could not start — try again.');
      this.state.dogs[this.state.scene] = j.dog.id; this.save();
      this.watchDog(j.dog.id, true);
    } catch (err) {
      if (epoch !== this.pollEpoch) return;
      this.state.busy = false; this.dogStatus(err.message, true); this.sync();
    }
  }

  watchDog(id, fresh) {
    this.stopPoll();
    const epoch = this.pollEpoch;
    const active = () => epoch === this.pollEpoch && this.state.dogs[this.state.scene] === id;
    const started = Date.now();
    let shown = false;              // the painted card has been announced (products may still be painting)
    const tick = async () => {
      if (!active()) return;
      try {
        const r = await fetch(`${this.api}?dog=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
        const j = await r.json().catch(() => ({}));
        if (!active()) return;
        if (!r.ok || !j.dog) { if (r.status === 404) { delete this.state.dogs[this.state.scene]; this.save(); } throw new Error(j.error || 'lost'); }
        const d = j.dog;
        if (d.scene !== this.state.scene) return; // the customer moved on
        this.state.dog = d; this.restoreFresh();
        if (d.status === 'ready') {
          this.state.busy = false; this.el.busy.hidden = true;
          // the matching ornament + mug are painted after the card — keep polling (quietly) for them
          if (d.products === 'queued' || d.products === 'drawing') this.state.poll = setTimeout(tick, 4000);
          else this.state.poll = null;
          const announce = !shown && fresh;
          if (announce) this.dogStatus(`Here's your dog${(d.dogs || 1) > 1 ? 's' : ''} in ${this.scene()?.label || 'the scene'}. Not quite them? Try again (${Math.max(0, d.max_tries - d.tries)} left) — or try a clearer photo.`);
          shown = true;
          this.showStage(); this.sync();
          this.showSavedMugStatus();
          if (this.fromCalendar && this.giftMode === 'mug' && !this.fresh() && !this.restoringDesign) await this.preview();
          if (announce && window.matchMedia('(max-width: 880px)').matches) this.el.stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (d.status === 'failed') {
          this.state.busy = false; this.stopPoll(); this.el.busy.hidden = true;
          this.dogStatus(d.error || (this.fromCalendar && this.giftMode === 'mug' ? 'Your mug artwork couldn’t finish. Choose “Paint my dog” to try again.' : 'The drawing did not come out — try again.'), true); this.sync();
          return;
        }
        this.state.busy = true; this.el.busy.hidden = false;
        const secs = Math.round((Date.now() - started) / 1000);
        this.el.busyText.textContent = secs < 70 ? `Painting your dog${this.state.count > 1 ? 's' : ''} into the scene…` : 'Almost there — we check every drawing before you see it…';
        this.dogStatus(secs < 70 ? (this.fromCalendar && this.giftMode === 'mug' ? 'Painting your dog into their mug artwork — about a minute. You’ll see their preview before choosing a mug size.' : 'Painting your dog into the scene — about a minute. You can write the inside while you wait.') : 'Almost there — we check every drawing before you see it…');
        this.el.bar.style.width = `${Math.min(94, 8 + secs * 1.1)}%`;
        this.sync();
      } catch { if (!active()) return; /* keep polling a little longer */ }
      if (!active()) return;
      if (Date.now() - started < 8 * 60_000) this.state.poll = setTimeout(tick, 3500);
      else { this.state.busy = false; this.el.busy.hidden = true; this.dogStatus('This is taking longer than usual — refresh in a minute; your drawing keeps going.', true); this.sync(); }
    };
    if (fresh) { this.el.busy.hidden = false; this.el.busyText.textContent = 'Painting your dog into the scene…'; this.el.bar.style.width = '6%'; }
    tick();
  }
  stopPoll() { this.pollEpoch = (this.pollEpoch || 0) + 1; if (this.state.poll) clearTimeout(this.state.poll); this.state.poll = null; }
  dogReady() { const d = this.state.dog; return !!d && d.status === 'ready' && d.scene === this.state.scene; }

  // ---- words -------------------------------------------------------------------------
  renderGreetings(pick) {
    const gs = this.state.manifest.greetings || [];
    this.state.greeting = gs.some((g) => g.id === pick) ? pick : (gs[0] || {}).id;
    this.el.greetings.innerHTML = '';
    for (const g of gs) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'lig-cm__chip'; b.textContent = g.label; b.dataset.id = g.id;
      b.setAttribute('aria-pressed', String(g.id === this.state.greeting));
      b.addEventListener('click', () => { this.applyGreeting(g.id); this.save(); this.sync(); });
      this.el.greetings.append(b);
    }
  }
  applyGreeting(id) {
    const g = (this.state.manifest.greetings || []).find((x) => x.id === id);
    if (!g) return;
    this.state.greeting = id;
    this.el.greetings.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.id === id)));
    this.el.headline.value = g.headline; this.el.body.value = g.body; this.el.signoff.value = g.signoff;
    this.counts();
  }
  words() { return { headline: this.el.headline.value.trim(), body: this.el.body.value.trim(), signoff: this.el.signoff.value.trim(), names: this.el.names.value.trim() }; }
  setWords(w) { ['headline', 'body', 'signoff', 'names'].forEach((k) => { if (typeof w[k] === 'string') this.el[k].value = w[k]; }); }
  counts() { this.el.counters.forEach((c) => { const f = this.el[c.dataset.cmCount]; if (f) c.textContent = `${[...f.value].length}/${f.maxLength}`; }); }

  key() { return JSON.stringify({ s: this.state.scene, m: this.state.mode, d: this.state.mode === 'dog' ? (this.state.dog?.id || '') + ':' + (this.state.dog?.front_url || '') : '', p: this.el.paw.checked, ...this.words() }); }
  fresh() { return !!this.state.design && this.state.designKey === this.key(); }

  // ---- the card ----------------------------------------------------------------------
  async preview() {
    if (this.state.busy) return;
    const w = this.words();
    if (this.state.mode === 'dog' && !this.dogReady()) { this.status('Paint your dog first — or choose "Keep our golden".', true); return; }
    if (!w.names) { this.status('Add the names you would like to sign the cards with.', true); this.el.names.focus(); return; }
    if (!w.headline && !w.body) { this.status('Add a greeting or a message.', true); this.el.headline.focus(); return; }
    const previewEpoch = this.pollEpoch;
    this.state.busy = true; this.sync();
    this.el.busy.hidden = false; this.el.busyText.textContent = this.giftMode === 'mug' ? 'Setting your mug preview…' : 'Setting your card…'; this.el.bar.style.width = '60%';
    const key = this.key();
    try {
      const r = await fetch(this.api, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ action: 'preview', front: this.state.scene, greeting: this.state.greeting, words: w, paw: this.el.paw.checked, ...(this.state.mode === 'dog' ? { dog_id: this.state.dog.id } : {}), hp_4c1: this.el.hp?.value || '' }),
      });
      const d = await r.json().catch(() => ({}));
      if (this.key() !== key || previewEpoch !== this.pollEpoch) return;
      if (!r.ok || !d.design) throw new Error(d.error || (this.giftMode === 'mug' ? 'Your mug preview could not be prepared just now — try again.' : 'The card could not be set just now — try again.'));
      this.state.design = d.design;
      this.setWords(d.design.words); this.counts();
      this.state.designKey = this.key();
      this.el.dropped.hidden = !(d.dropped && d.dropped.length);
      if (d.dropped && d.dropped.length) this.el.dropped.textContent = `We left out ${d.dropped.join(' ')} — emoji and some symbols can't be printed in the card's lettering.`;
      this.showStage();
      this.showSet();
      this.status(this.giftMode === 'mug' ? 'Here is your Christmas mug. Check your dog’s likeness before adding it to your cart.' : 'Here is your card. Check every word — it prints exactly like this.');
      this.save();
      if (window.matchMedia('(max-width: 880px)').matches) this.el.stage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      if (this.key() === key && previewEpoch === this.pollEpoch) this.status(err.message, true);
    } finally {
      if (previewEpoch === this.pollEpoch) { this.state.busy = false; this.el.busy.hidden = true; this.sync(); }
    }
  }

  async restoreDesign(id) {
    const request = { epoch: this.pollEpoch, source: JSON.stringify([this.state.scene, this.state.mode, this.state.photoUrls, this.state.count, this.words(), this.el.paw.checked]) };
    this.restoringDesign = request;
    const current = () => this.restoringDesign === request && request.epoch === this.pollEpoch && request.source === JSON.stringify([this.state.scene, this.state.mode, this.state.photoUrls, this.state.count, this.words(), this.el.paw.checked]);
    try {
      const r = await fetch(`${this.api}?id=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
      const j = await r.json();
      if (!current() || !r.ok || !j.design) return;
      const d = j.design;
      if (d.front !== this.state.scene || d.your_dog !== (this.state.mode === 'dog') || JSON.stringify(d.words) !== JSON.stringify(this.words()) || d.paw !== this.el.paw.checked) return;
      this.state.design = d; this.state.designKey = '';
      this.restoreFresh(); this.showStage(); this.sync();
      this.showSavedMugStatus();
    } catch { /* start fresh */ }
    finally {
      const resume = current();
      if (this.restoringDesign === request) this.restoringDesign = null;
      if (resume && this.fromCalendar && this.giftMode === 'mug' && this.dogReady() && !this.fresh() && !this.state.busy) await this.preview();
    }
  }

  showSavedMugStatus() {
    if (!this.fromCalendar || this.giftMode !== 'mug' || !this.fresh()) return;
    this.status('Here is your Christmas mug. Check your dog’s likeness before adding it to your cart.');
    this.dogStatus('Their saved artwork is ready.');
  }

  restoreFresh() {
    const d = this.state.design;
    if (!d || d.front !== this.state.scene || d.your_dog !== (this.state.mode === 'dog') || JSON.stringify(d.words) !== JSON.stringify(this.words()) || d.paw !== this.el.paw.checked) return;
    if (d.your_dog && (!this.dogReady() || d.front_url !== this.state.dog.front_url)) return;
    this.state.designKey = this.key();
  }

  /** The set's pictures: the MUG comes with the card (it's the card's painting); the ornaments — one
   *  per dog — are the design's own (our golden), or the dog drawing's, a minute after the card. */
  setArt() {
    const d = this.state.design;
    const g = this.state.dog;
    if(!d||!this.fresh()){const live=this.state.mode==='dog'&&g&&g.scene===this.state.scene&&g.status==='ready'?g:null;if(!live)return null;return {mug:live.mug_preview_url||null,orns:live.ornament_previews?.length?live.ornament_previews:live.ornament_preview_url?[live.ornament_preview_url]:[],breeds:live.breeds||[]};}
    const live = d.your_dog && g && g.scene === d.front && g.status === 'ready' ? g : null;
    const mug = d.mug_preview_url || live?.mug_preview_url || null;
    const list = (x) => (x?.ornament_previews?.length ? x.ornament_previews : x?.ornament_preview_url ? [x.ornament_preview_url] : []);
    const orns = list(d).length ? list(d) : list(live);
    return { mug, orns, breeds: live?.breeds || [] };
  }
  showSet() {
    const a = this.setArt() || { mug: null, orns: [], breeds: [] };
    if (a.mug && this.el.mugImg.src !== a.mug) this.el.mugImg.src = a.mug;
    this.el.mugImg.hidden = !a.mug;
    const want = this.state.mode==='dog' ? (this.state.dog?.dogs || this.state.count) : 1;
    const key = JSON.stringify(a.orns);
    if (key !== this.state.ornKey) {
      this.state.ornKey = key;
      this.el.orns.innerHTML = '';
      a.orns.forEach((src, i) => {
        const f = document.createElement('figure');
        const who = a.orns.length > 1 ? `Dog ${i + 1}${a.breeds[i] ? ' · ' + a.breeds[i] : ''}` : '';
        f.innerHTML = `<img alt="The matching ornament${who ? ' — ' + esc(who) : ''}" width="780" height="780"><figcaption>Ceramic ornament · 2.75″${who ? ' · ' + esc(who) : ''}</figcaption><button type="button" class="lig-cm__btn" data-cm-add-orn="${i + 1}">Add the ornament</button>`;
        f.querySelector('img').src = src;
        f.querySelector('button').addEventListener('click', (e) => { e.preventDefault(); this.addMatch('ornament', i + 1); });
        this.el.orns.append(f);
      });
    }
    if (this.el.setWait) {
      const waiting = !this.fromCalendar && a.orns.length < want && this.state.mode==='dog'&&!!this.state.dog;
      this.el.setWait.hidden = !waiting;
      const failed = waiting && this.state.dog?.products === 'failed';
      this.el.setWait.querySelector('[data-cm-set-wait-text]').textContent = failed
        ? 'The ornament didn’t come out cleanly this time — your card and mug are fine. Paint again for a fresh set, or order and write to us.'
        : `Painting ${want > 1 ? 'each dog onto its own ornament' : 'your dog onto the ornament'} — about a minute. Write the inside while you wait.`;
      this.el.setWait.querySelector('.lig-om__spinner').style.display = failed ? 'none' : 'inline-block';
    }
  }

  cardVariant() {
    const want = this.state.mode === 'dog' ? /your dog/i : /our golden/i;
    return this.products.card.find((v) => want.test(v.title)) || null;
  }

  sync() {
    const fresh = this.fresh();
    const v = this.cardVariant();
    const qty = Math.max(1, Math.min(4, parseInt(this.el.qty.value, 10) || 1));
    const dogMode = this.state.mode === 'dog';
    this.el.paint.disabled = this.state.busy || this.state.uploading || !this.photosReady();
    this.el.paint.hidden = dogMode && this.dogReady();
    this.el.again.hidden = !(dogMode && this.dogReady() && this.state.dog.tries < this.state.dog.max_tries);
    this.el.again.disabled = this.state.busy;
    this.el.see.textContent = this.state.design && !fresh ? 'Update my card' : 'See my card';
    this.el.see.disabled = this.state.busy || (dogMode && !this.dogReady());
    if (this.el.mugPreview) { this.el.mugPreview.hidden = fresh; this.el.mugPreview.disabled = this.state.busy || !this.dogReady(); }
    this.el.price.textContent = v ? `${money(v.price)} for 25 cards · ${dogMode ? 'with your dog' : 'with our golden'}` : '';
    this.el.add.disabled = !fresh || !v || this.state.busy || this.state.cartBusy;
    this.el.total.textContent = v ? `${money(v.price * qty)} for ${qty * 25} cards, envelopes included` : '';
    const art = this.setArt();
    this.el.set.hidden = !fresh && !this.dogReady();
    this.showSet();
    const orn = this.products.ornament[0], mugV = this.mugVariant();
    this.el.orns.querySelectorAll('[data-cm-add-orn]').forEach((b) => { b.disabled = !orn || this.state.cartBusy || this.state.busy; b.textContent = orn ? `${fresh?'Add':'Finish card & add'} the ornament · ${money(orn.price)}` : 'Ornament unavailable'; });
    this.el.addMug.disabled = !art?.mug || this.state.cartBusy || this.state.busy;
    this.el.addMug.textContent = mugV ? `${fresh?'Add':'Finish card & add'} the mug · ${money(mugV.price)}` : 'Mug unavailable';
    if (this.state.design && !fresh && !this.state.busy) this.status(this.giftMode === 'mug' ? 'See the updated mug preview before adding it to your cart.' : 'You changed something — see the card again before adding it to your cart.');
  }

  mugVariant() {
    const size = this.el.mugSize?.value || '11 oz';
    return this.products.mug.find((v) => v.title === size) || this.products.mug[0] || null;
  }

  async cartAdd(items, what) {
    this.state.cartBusy = true; this.sync();
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((n) => n.getAttribute('data-section-id')).join(',');
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections, sections_url: window.location.pathname }) });
      const parsed = await response.json().catch(() => ({}));
      if (!response.ok || parsed.status) {
        document.dispatchEvent(new CartErrorEvent('lig-card-maker', parsed.message, parsed.description, parsed.errors));
        this.status(parsed.description || parsed.message || 'Could not add to the cart.', true);
        return false;
      }
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => null);
      document.dispatchEvent(new CartAddEvent(cart ?? parsed, 'lig-card-maker', { source: 'lig-card-maker', itemCount: cart?.item_count, sections: parsed.sections }));
      const standaloneMug = what === 'mug' && this.fromCalendar && this.giftMode === 'mug';
      if (what === 'card' || standaloneMug) { const drawer = document.querySelector('cart-drawer-component'); if (drawer && typeof drawer.open === 'function') { try { drawer.open(); } catch { /* inline cart link below */ } } }
      if (standaloneMug && this.el.cartContinuation) this.el.cartContinuation.hidden = false;
      this.status(what === 'card' ? 'Your cards are in the cart. Make it a set?' : `The ${what} is in your cart.`);
      this.refreshShip(cart);
      return true;
    } catch {
      document.dispatchEvent(new CartErrorEvent('lig-card-maker', 'Could not add to cart', undefined, undefined));
      this.status('Could not add to the cart — try again.', true);
      return false;
    } finally {
      this.state.cartBusy = false; this.sync();
    }
  }

  props(extra) {
    const d = this.state.design, s = this.scene();
    return { _card_id: d.id, Scene: s?.label || d.front, Dog: d.your_dog ? 'Your dog' : 'Our golden', ...extra };
  }

  async addCard() {
    const d = this.state.design, v = this.cardVariant();
    if (!d || !this.fresh() || !v) return;
    const qty = Math.max(1, Math.min(4, parseInt(this.el.qty.value, 10) || 1));
    const ok = await this.cartAdd([{ id: v.id, quantity: qty, properties: this.props({ Greeting: d.words.headline || d.words.body.slice(0, 40), Signed: d.words.names.replace(/\n/g, ' '), _preview: d.preview_url }) }], 'card');
    if (ok) { this.state.inCart = true; this.el.set.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
  }

  async addMatch(kind, dog = 1) {
    if(!this.fresh()){if(this.state.busy)return;await this.preview();}
    const d = this.state.design;
    if (!d || !this.fresh()) return;
    const v = kind === 'ornament' ? this.products.ornament[0] : this.mugVariant();
    if (!v) return;
    const a = this.setArt();
    const pic = kind === 'ornament' ? a?.orns[dog - 1] : a?.mug;
    if (!pic) return;
    const extra = kind === 'ornament' ? { _dog: String(dog), ...(a.orns.length > 1 ? { Dog: `Dog ${dog}${a.breeds[dog - 1] ? ' · ' + a.breeds[dog - 1] : ''}` } : {}) } : {};
    await this.cartAdd([{ id: v.id, quantity: 1, properties: { ...this.props({ _preview: pic }), ...extra } }], kind);
  }

  async refreshShip(cart) {
    try {
      const c = cart || await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const total = c?.total_price ?? 0;
      if (!this.el.ship) return;
      this.el.ship.textContent = total >= this.freeShip ? 'Your order ships free.' : total > 0 ? `You're ${money(this.freeShip - total)} away from free shipping — the ornament and mug get you there.` : `Orders over ${money(this.freeShip)} ship free — cards plus the ornament get you there.`;
    } catch { /* optional */ }
  }

  // ---- small things --------------------------------------------------------------------
  status(msg, err = false) { this.el.status.textContent = msg; this.el.status.classList.toggle('is-error', !!err); }
  dogStatus(msg, err = false) { this.el.dogStatus.textContent = msg; this.el.dogStatus.classList.toggle('is-error', !!err); }
  save() {
    try { localStorage.setItem(this.fromCalendar ? 'lig_card_calendar_' + this.giftMode + '_v1' : STORE_KEY, JSON.stringify({ scene: this.state.scene, mode: this.state.mode, photoUrls: this.state.photoUrls, privateIntake: this.privateIntake, count: this.state.count, dogs: this.state.dogs, greeting: this.state.greeting, words: this.words(), paw: this.el.paw.checked, designId: this.state.design?.id || null })); } catch { /* private mode */ }
  }
  load() { try { return JSON.parse(localStorage.getItem(this.fromCalendar ? 'lig_card_calendar_' + this.giftMode + '_v1' : STORE_KEY) || '{}') || {}; } catch { return {}; } }
}

function downscale(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
        const scale = Math.min(1, DOWNSCALE_MAX_EDGE / Math.max(w, h));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(w * scale)); canvas.height = Math.max(1, Math.round(h * scale));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob ? { blob, contentType: 'image/jpeg', ext: 'jpg' } : rawFallback(file)), 'image/jpeg', DOWNSCALE_QUALITY);
      } catch { resolve(rawFallback(file)); }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(rawFallback(file)); }; // HEIC on a non-Safari browser: the middleware converts
    img.src = url;
  });
}
function rawFallback(file) {
  if (file.size > 12 * 1024 * 1024) return null;
  const ct = /^image\/(jpe?g|png|webp|heic|heif)$/i.test(file.type || '') ? file.type : 'image/jpeg';
  const ext = ((file.name || '').match(/\.(jpe?g|png|webp|heic|heif)$/i) || ['', 'jpg'])[1] || 'jpg';
  return { blob: file, contentType: ct, ext: ext.toLowerCase() };
}

document.querySelectorAll('[data-lig-cm]').forEach((root) => new CardMaker(root));
